import { ChevronRight, MailOpen } from "lucide-react";
import { Link, Navigate } from "react-router";

import { ROUTES, routeTo } from "@/app/routes";
import { ROLE_LABEL } from "@/core/brand/brand-context";
import { useMyBrands } from "@/core/brand/my-brands";
import { useDocumentTitle } from "@/core/hooks/use-document-title";
import { FullScreen } from "@/features/auth/RequireSignIn";
import { InvitationsNotice } from "@/features/invitations/InvitationsNotice";
import { SimpleLayout } from "@/layouts/SimpleLayout";
import { Badge } from "@/ui/badge";
import { BrandMark } from "@/ui/brand-mark";
import { Card } from "@/ui/card";
import { Spinner } from "@/ui/spinner";
import { ErrorState, PageHeader } from "@/ui/states";

import { BRAND_STATUS_DISPLAY } from "./brand-display";

/** Where the portal opens: the only brand straight away, a choice between several, or invitations. */
export function StartPage() {
  useDocumentTitle("Your brands");
  const brands = useMyBrands();

  if (brands.isPending) {
    return (
      <FullScreen>
        <Spinner label="Loading your brands…" />
      </FullScreen>
    );
  }
  if (brands.isError) {
    return (
      <SimpleLayout>
        <ErrorState error={brands.error} onRetry={() => void brands.refetch()} />
      </SimpleLayout>
    );
  }
  const list = brands.data;
  if (list.length === 0) return <Navigate to={ROUTES.invitations} replace />;
  if (list.length === 1) return <Navigate to={routeTo.brandHome(list[0]!.brand.id)} replace />;

  return (
    <SimpleLayout>
      <PageHeader title="Your brands" subtitle="Choose the kitchen you're working on." />
      <InvitationsNotice />
      <Card className="p-0">
        <ul aria-label="Your brands">
          {list.map(({ brand, role }) => {
            const status = BRAND_STATUS_DISPLAY[brand.status];
            return (
              <li key={brand.id} className="border-t border-border-subtle first:border-t-0">
                <Link
                  to={routeTo.brandHome(brand.id)}
                  className="flex min-h-16 items-center gap-3.5 px-4 py-3 text-ink no-underline hover:bg-surface-muted sm:px-5"
                >
                  <BrandMark name={brand.name} logoUrl={brand.logoUrl} />
                  <span className="flex min-w-0 grow flex-col gap-0.5">
                    <span className="truncate font-bold">{brand.name}</span>
                    <span className="text-[13px] text-muted">{ROLE_LABEL[role]}</span>
                  </span>
                  {brand.status !== "ACTIVE" && <Badge tone={status.tone}>{status.label}</Badge>}
                  <ChevronRight className="size-5 shrink-0 text-faint" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
      </Card>
      <Link to={ROUTES.invitations} className="flex items-center gap-2 self-start text-sm font-bold">
        <MailOpen className="size-4" aria-hidden="true" />
        Join another brand
      </Link>
    </SimpleLayout>
  );
}
